import type { ClientSideFormToolOutput } from '@kanbu/schema/contracts';
import { useLingui, Trans } from '@lingui/react/macro';
import { useNavigate } from '@tanstack/react-router';
import { Button, Input, Label, Switch, TextArea, toast } from '@utima/ui';
import { useEffect, useMemo, useState } from 'react';

import { FormPage } from '@/components/page/FormPage';
import { trpc } from '@/services/trpc';

import { FormFieldList } from './FormFieldList';
import { FormPreviewPanel } from './FormPreviewPanel';
import { FormToolTemplatesSection } from './FormToolTemplates';
import { useClientSideFormToolMutation } from '../hooks/useClientSideFormToolMutation';
import {
  createEmptyFormState,
  formStateToArtifact,
  type FormToolFormState,
} from '../lib/formFieldDefaults';
import {
  useFormToolTemplates,
  type FormToolTemplate,
} from '../lib/formToolTemplates';
import { validateFormToolForm } from '../lib/formToolValidation';

export interface FormToolEditorProps {
  formToolId?: string;
  initialTemplateId?: string;
}

function outputToFormState(
  output: ClientSideFormToolOutput,
): FormToolFormState {
  return createEmptyFormState({
    name: output.name,
    description: output.description,
    formTitle: output.formTitle,
    formDescription: output.formDescription ?? '',
    submitLabel: output.submitLabel,
    presentation: output.presentation,
    disableDismiss: output.disableDismiss,
    fields: output.fields,
    availableToAllChats: output.availableToAllChats,
    chatIds: output.chatIds ?? [],
    isActive: output.isActive,
  });
}

export function FormToolEditor({
  formToolId,
  initialTemplateId,
}: FormToolEditorProps) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const templates = useFormToolTemplates();
  const isEdit = Boolean(formToolId);
  const { create, update } = useClientSideFormToolMutation();

  const { data: existing, isLoading } =
    trpc.clientSideFormTools.findOne.useQuery(
      { id: formToolId! },
      { enabled: isEdit },
    );

  const { data: chats } = trpc.chats.findAll.useQuery({
    pagination: { pageSize: 1000 },
    fields: ['id', 'name'],
  });

  const [form, setForm] = useState<FormToolFormState>(() =>
    createEmptyFormState({ submitLabel: t`Submit` }),
  );
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (isEdit && existing && !initialized) {
      setForm(outputToFormState(existing));
      setInitialized(true);
    }
  }, [existing, initialized, isEdit]);

  useEffect(() => {
    if (!isEdit && initialTemplateId && !initialized) {
      const template = templates.find(item => item.id === initialTemplateId);

      if (template) {
        setForm(createEmptyFormState(template.defaults));
      }

      setInitialized(true);
    }
  }, [initialTemplateId, initialized, isEdit, templates]);

  const setField = <K extends keyof FormToolFormState>(
    key: K,
    value: FormToolFormState[K],
  ) => setForm(prev => ({ ...prev, [key]: value }));

  const toggleChat = (chatId: string) =>
    setForm(prev => ({
      ...prev,
      chatIds: prev.chatIds.includes(chatId)
        ? prev.chatIds.filter(id => id !== chatId)
        : [...prev.chatIds, chatId],
    }));

  const artifact = useMemo(() => formStateToArtifact(form), [form]);

  const handleSave = async () => {
    const validationError = validateFormToolForm(form);

    if (validationError) {
      toast.error(validationError);

      return;
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      formTitle: form.formTitle.trim(),
      formDescription: form.formDescription.trim() || undefined,
      submitLabel: form.submitLabel.trim() || t`Submit`,
      presentation: form.presentation,
      disableDismiss: form.disableDismiss,
      fields: form.fields,
      availableToAllChats: form.availableToAllChats,
      chatIds: form.availableToAllChats ? [] : form.chatIds,
      isActive: form.isActive,
    };

    try {
      if (isEdit && formToolId) {
        await update.mutateAsync({ id: formToolId, ...payload });
        toast.success(t`Form tool saved.`);
      } else {
        const created = await create.mutateAsync(payload);
        toast.success(t`Form tool created.`);
        await navigate({
          to: '/agent-tools/client-side/$formToolId/edit',
          params: { formToolId: created.id },
        });
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t`Failed to save form tool.`,
      );
    }
  };

  const applyTemplate = (template: FormToolTemplate) => {
    setForm(createEmptyFormState(template.defaults));
  };

  const chatItems = (chats?.items ?? []) as { id: string; name: string }[];
  const isPending = create.isPending || update.isPending;

  if (isEdit && isLoading) {
    return (
      <FormPage
        title={t`Edit form tool`}
        breadcrumbs={[
          {
            label: t`Agent Tools`,
            to: '/agent-tools',
            search: { tab: 'client-side' },
          },
          { label: t`Edit form tool` },
        ]}
      >
        <p className='text-sm text-muted-fg'>
          <Trans>Loading…</Trans>
        </p>
      </FormPage>
    );
  }

  return (
    <FormPage
      title={isEdit ? t`Edit form tool` : t`Create form tool`}
      breadcrumbs={[
        {
          label: t`Agent Tools`,
          to: '/agent-tools',
          search: { tab: 'client-side' },
        },
        { label: isEdit ? t`Edit form tool` : t`Create form tool` },
      ]}
      actions={
        <div className='flex gap-2'>
          <Button
            variant='secondary'
            onClick={() =>
              navigate({ to: '/agent-tools', search: { tab: 'client-side' } })
            }
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button loading={isPending} onClick={handleSave}>
            <Trans>Save</Trans>
          </Button>
        </div>
      }
    >
      {isEdit ? null : (
        <div className='mb-8'>
          <FormToolTemplatesSection onSelect={applyTemplate} />
        </div>
      )}

      <div className='grid gap-8 xl:grid-cols-[minmax(0,1fr)_360px]'>
        <div className='space-y-8'>
          <section className='space-y-4'>
            <div>
              <h2 className='text-base font-semibold'>
                <Trans>Tool settings (for the agent)</Trans>
              </h2>
              <p className='text-sm text-muted-fg'>
                <Trans>
                  The agent uses the name and description to decide when to show
                  this form.
                </Trans>
              </p>
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='tool-name'>
                <Trans>Tool name</Trans>
              </Label>
              <Input
                id='tool-name'
                value={form.name}
                onChange={event => setField('name', event.target.value)}
                placeholder={t`e.g. collect_contact_details`}
              />
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='tool-description'>
                <Trans>Description</Trans>
              </Label>
              <TextArea
                id='tool-description'
                value={form.description}
                onChange={event => setField('description', event.target.value)}
                rows={4}
                placeholder={t`When should the agent show this form?`}
              />
              <p className='text-xs text-muted-fg'>
                <Trans>
                  Include what the agent should do after the user submits — e.g.
                  store the data via an integration tool, then thank the user
                  and continue the workflow before the final answer.
                </Trans>
              </p>
            </div>

            <div className='flex flex-wrap items-center gap-6'>
              <div className='flex items-center gap-2'>
                <Switch
                  checked={form.isActive}
                  onCheckedChange={checked => setField('isActive', checked)}
                />
                <Label>
                  <Trans>Active</Trans>
                </Label>
              </div>

              <div className='flex items-center gap-2'>
                <Switch
                  checked={form.availableToAllChats}
                  onCheckedChange={checked =>
                    setField('availableToAllChats', checked)
                  }
                />
                <Label>
                  <Trans>Available to all chats</Trans>
                </Label>
              </div>
            </div>

            {form.availableToAllChats ? null : (
              <div className='space-y-2 rounded-radius border border-border p-3'>
                <Label>
                  <Trans>Assigned chats</Trans>
                </Label>
                <div className='max-h-40 space-y-2 overflow-y-auto'>
                  {chatItems.map(chat => (
                    <label
                      key={chat.id}
                      className='flex items-center gap-2 text-sm'
                    >
                      <input
                        type='checkbox'
                        checked={form.chatIds.includes(chat.id)}
                        onChange={() => toggleChat(chat.id)}
                      />
                      {chat.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className='space-y-4'>
            <div>
              <h2 className='text-base font-semibold'>
                <Trans>Form content (for the user)</Trans>
              </h2>
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='form-title'>
                <Trans>Form title</Trans>
              </Label>
              <Input
                id='form-title'
                value={form.formTitle}
                onChange={event => setField('formTitle', event.target.value)}
              />
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='form-description'>
                <Trans>Form description</Trans>
              </Label>
              <TextArea
                id='form-description'
                value={form.formDescription}
                onChange={event =>
                  setField('formDescription', event.target.value)
                }
                rows={2}
              />
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='submit-label'>
                <Trans>Submit button label</Trans>
              </Label>
              <Input
                id='submit-label'
                value={form.submitLabel}
                onChange={event => setField('submitLabel', event.target.value)}
              />
            </div>

            <div className='space-y-2'>
              <Label>
                <Trans>Presentation</Trans>
              </Label>
              <div className='flex flex-wrap gap-4'>
                {(['inline', 'overlay'] as const).map(value => (
                  <label
                    key={value}
                    className='flex items-center gap-2 text-sm'
                  >
                    <input
                      type='radio'
                      name='presentation'
                      checked={form.presentation === value}
                      onChange={() => setField('presentation', value)}
                    />
                    {value === 'inline' ? (
                      <Trans>Inline in chat</Trans>
                    ) : (
                      <Trans>Overlay sheet</Trans>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div className='flex items-center gap-2'>
              <Switch
                checked={form.disableDismiss}
                onCheckedChange={checked => setField('disableDismiss', checked)}
              />
              <Label>
                <Trans>Require completion before chatting</Trans>
              </Label>
            </div>

            <FormFieldList
              fields={form.fields}
              savedKeys={existing?.fields.map(field => field.name)}
              onChange={fields => setField('fields', fields)}
            />
          </section>
        </div>

        <FormPreviewPanel artifact={artifact} />
      </div>
    </FormPage>
  );
}
